/**
 * @desc: useFilterablePage — 页面筛选条件初始化 Hook
 *
 * 在页面挂载时通过可扩展的初始化来源（URL / 短链接 / 继承等）
 * 获取筛选条件，按指定策略合并后作为页面筛选器初始值。
 * Hook 不维护页面当前筛选器值，只提供初始值获取方法和同步方法。
 */
import { useRef, useState } from 'react';
import { useSelector, useDispatch } from 'umi';
import useMountedRef from '@intelligence/v3/_hooks/useMountedRef';
import { normalizeFilterValuesByItems } from '@intelligence/v3/filterControls/apiParams';
import {
  shouldAutoSyncShortLinkOnInit,
  getDefaultSyncTargets,
  mergeFilterValues,
  shouldClearSourceAfterApply,
  getInitMeta,
  getInheritedValuesByPageId,
} from './utils';
import { syncShortLink } from './syncTargets/shortLinkTarget';
import { FILTER_INHERIT_NAMESPACE, IFilterInheritState } from '@intelligence/models/filterInherit';
import { getDefaultSources } from './sources';
import { FilterMergeStrategy } from './enums';

import type { MutableRefObject } from 'react';
import type { IFilterItemConfig, IFilterValues } from '@intelligence/v3/filterCore/types';
import type {
  IFilterInitContext,
  IFilterInitMeta,
  IFilterInitSource,
  IFilterSyncResult,
  IFilterSyncTargetResult,
  IUseFilterablePageOptions,
  IUseFilterablePageResult,
  IResolvedLayer,
} from './types';

const createLatestRequestGuard = (
  requestIdRef: MutableRefObject<number>,
  mountedRef: MutableRefObject<boolean>,
): (() => boolean) => {
  const requestId = requestIdRef.current + 1;
  requestIdRef.current = requestId;

  return () => {
    return mountedRef.current && requestIdRef.current === requestId;
  };
};

/**
 * 并行解析所有初始化来源。
 * 单个来源失败只记录日志，不阻断其他来源和默认值回退。
 */
const collectResolvedLayers = async (
  initSources: IFilterInitSource[],
  items: IFilterItemConfig[],
  context: IFilterInitContext,
): Promise<IResolvedLayer[]> => {
  const results = await Promise.allSettled(
    initSources.map(async (source) => {
      const values = await source.resolve(items, context);

      return {
        source,
        priority: source.priority,
        values: normalizeFilterValuesByItems(items, values),
      };
    }),
  );

  return results.reduce<IResolvedLayer[]>((layers, result, index) => {
    if (result.status === 'fulfilled') {
      layers.push(result.value);
      return layers;
    }

    console.debug(`[dbi] [useFilterablePage] 来源 ${initSources[index].name} 解析失败`, result.reason);
    return layers;
  }, []);
};

const hasResolvedValues = (values?: IFilterValues): boolean => {
  return Object.keys(values || {}).length > 0;
};

const getSourceCleanupResult = (params: {
  mergeStrategy: FilterMergeStrategy;
  layers: IResolvedLayer[];
}): {
  clearedSources: string[];
  shouldClearInheritedValues: boolean;
} => {
  const { mergeStrategy, layers } = params;
  const clearedSources: string[] = [];

  const shouldClearInheritedValues = layers.some((layer) => {
    if (!layer.source.shouldClearAfterApply || !hasResolvedValues(layer.values)) {
      return false;
    }

    const shouldClearCurrentLayer = shouldClearSourceAfterApply({
      mergeStrategy,
      layers,
      targetLayer: layer,
    });

    if (shouldClearCurrentLayer) {
      clearedSources.push(layer.source.name);
    }

    return shouldClearCurrentLayer;
  });

  return {
    clearedSources,
    shouldClearInheritedValues,
  };
};

export default function useFilterablePage(options: IUseFilterablePageOptions): IUseFilterablePageResult {
  const {
    ready = true,
    pageId,
    items,
    defaultValues,
    mergeStrategy = FilterMergeStrategy.MERGE_BY_PRIORITY,
    sources,
    syncTargets,
    conditions,
    syncTargetConfig,
  } = options;

  const dispatch = useDispatch();
  const filterInheritState = useSelector<any, IFilterInheritState>((state) => state[FILTER_INHERIT_NAMESPACE] || {});
  const inheritedValues = getInheritedValuesByPageId(pageId, filterInheritState);

  const mountedRef = useMountedRef();
  const initRequestIdRef = useRef(0);
  const syncRequestIdRef = useRef(0);

  const [initMeta, setInitMeta] = useState<IFilterInitMeta | null>(null);

  /**
   * 同步 confirmed 筛选值到外部目标。
   * 目标可能是短链、继承状态或明文 URL 参数，按配置顺序执行。
   */
  const syncFilterState = async (values: IFilterValues): Promise<IFilterSyncResult> => {
    const isLatestSyncRequest = createLatestRequestGuard(syncRequestIdRef, mountedRef);
    const currentFilterValues = normalizeFilterValuesByItems(items, values);
    const context = {
      pageId,
      items,
      dispatch,
      shouldApply: isLatestSyncRequest,
    };
    const targets = syncTargets || getDefaultSyncTargets(syncTargetConfig);
    const results: IFilterSyncTargetResult[] = [];

    for (const target of targets) {
      if (!isLatestSyncRequest()) {
        results.push({
          name: target.name,
          success: true,
          skipped: true,
        });
        continue;
      }

      if (target.shouldSync && !target.shouldSync(context)) {
        results.push({
          name: target.name,
          success: true,
          skipped: true,
        });
        continue;
      }

      try {
        await target.sync(currentFilterValues, context);
        results.push({
          name: target.name,
          success: true,
        });
      } catch (error) {
        console.debug(`[dbi] [useFilterablePage] 同步目标 ${target.name} 执行失败`, error);
        results.push({
          name: target.name,
          success: false,
          error,
        });
      }
    }

    return {
      hasError: results.some((item) => !item.success),
      results,
    };
  };

  /**
   * 获取页面初始筛选值。
   * 解析默认值和多来源结果后合并，并在短链模式下把最终值回写为新 filter_short_id。
   */
  const getInitialFilterValues = async (): Promise<IFilterValues> => {
    const isLatestInitRequest = createLatestRequestGuard(initRequestIdRef, mountedRef);
    const normalizedDefaultValues = normalizeFilterValuesByItems(items, defaultValues);

    if (!ready) {
      return normalizedDefaultValues;
    }

    try {
      const initSources = sources || getDefaultSources();
      const effectiveInheritedValues = inheritedValues || {};
      const context = {
        pageId,
        dispatch,
        inheritedValues: effectiveInheritedValues,
        conditions,
      };
      const layers = await collectResolvedLayers(initSources, items, context);
      const hasExternalResolvedValues = layers.some((layer) => {
        return hasResolvedValues(layer.values);
      });

      const merged = normalizeFilterValuesByItems(
        items,
        mergeFilterValues(mergeStrategy, normalizedDefaultValues, layers),
      );
      const initSyncContext = {
        pageId,
        items,
        dispatch,
      };
      const { clearedSources, shouldClearInheritedValues } = getSourceCleanupResult({
        mergeStrategy,
        layers,
      });

      if (isLatestInitRequest()) {
        setInitMeta(getInitMeta(mergeStrategy, layers, clearedSources));

        if (shouldClearInheritedValues && hasResolvedValues(effectiveInheritedValues)) {
          dispatch({
            type: `${FILTER_INHERIT_NAMESPACE}/clearInheritedState`,
          });
        }
      }

      if (
        isLatestInitRequest() &&
        hasExternalResolvedValues &&
        shouldAutoSyncShortLinkOnInit({
          syncTargets,
          syncTargetConfig,
        })
      ) {
        try {
          await syncShortLink(merged, initSyncContext, isLatestInitRequest);
        } catch (error) {
          console.debug('[dbi] [useFilterablePage] 初始化短链接同步失败', error);
        }
      }

      return merged;
    } catch (error) {
      console.debug('[dbi] [useFilterablePage] 初始化失败', error);

      if (isLatestInitRequest()) {
        setInitMeta({
          mergeStrategy,
          layers: [],
          fieldSources: {},
          clearedSources: [],
        });
      }

      return normalizedDefaultValues;
    }
  };

  return {
    initMeta,
    getInitialFilterValues,
    syncFilterState,
  };
}
